import { useEffect, useState, useRef } from 'react';
import { geoServer, geoServerDefaultParams } from '../services/geoServer';
import { transformGeoJSONToGrib2Json } from '../Components/WindyLayer/utils/transformGeoJSONToGrib2Json';
import { WindGeoJSON, Grib2JsonData } from '../Components/WindyLayer/types';

interface UseWindDataFetchReturn {
  windData: Grib2JsonData[] | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch and transform wind data from GeoServer
 *
 * @param typename - GeoServer layer name
 * @param workspace - GeoServer workspace
 * @param timestamp - Optional timestamp filter (milliseconds). If provided, only fetch data for this timestamp
 * @returns Object containing windData (grib2json format), isLoading state, and error
 */
export function useWindDataFetch(
  typename: string,
  workspace: string,
  timestamp?: string
): UseWindDataFetchReturn {
  const [windData, setWindData] = useState<Grib2JsonData[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Use ref to track latest data to avoid stale closures
  const dataCache = useRef<Map<string, Grib2JsonData[]>>(new Map());

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Check cache first
        const cacheKey = `${typename}:${timestamp || 'latest'}`;
        if (dataCache.current.has(cacheKey)) {
          if (isMounted) {
            setWindData(dataCache.current.get(cacheKey)!);
            setIsLoading(false);
          }
          return;
        }

        // The whole grid for one timestamp is fetched — not just the viewport.
        // The particle animation needs the full grid to keep covering the map
        // while the user pans, and a viewport-sized grid can shrink below the 2
        // points per axis the grid detector needs at high zoom. The timestamp
        // filter (always set by RenderGeoServerLayers) is what bounds the size.
        const params: any = {
          ...geoServerDefaultParams,
          typename: `${workspace}:${typename}`,
          outputFormat: 'application/json',
          srsName: 'EPSG:4326',
        };

        // Convert timestamp string to number if needed
        const timestampNum =
          timestamp !== undefined
            ? typeof timestamp === 'string'
              ? parseInt(timestamp, 10)
              : timestamp
            : undefined;
        if (timestampNum !== undefined) {
          params.cql_filter = `timestamp=${timestampNum}`;
        }

        // Fetch GeoJSON from GeoServer
        const response = await geoServer.get('wfs', { params });

        if (!isMounted) return;

        const geoJSON: WindGeoJSON = response.data;

        // Validate response
        if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
          throw new Error('No wind data returned from server');
        }

        // Transform GeoJSON to grib2json format
        const transformed = transformGeoJSONToGrib2Json(geoJSON, timestampNum);

        // Cache the transformed data
        dataCache.current.set(cacheKey, transformed);

        // Limit cache size to prevent memory issues
        if (dataCache.current.size > 10) {
          const firstKey = dataCache.current.keys().next().value;
          dataCache.current.delete(firstKey);
        }

        if (isMounted) {
          setWindData(transformed);
        }
      } catch (err) {
        console.error('[useWindDataFetch] Error fetching wind data:', err);
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
    // Re-fetch when typename, workspace, or timestamp changes
  }, [typename, workspace, timestamp]);

  return { windData, isLoading, error };
}
